import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { ITextItemLinkValue } from '@fi-shared/fi-text-item-helper/fi-text-item-link';
import { INameIdObj, INameIdObjWithChildren } from '@fi-shared/fi-shared-interfaces';
import { AppTextService } from '@fi-shared/app-text';
import { getObjectTypeNameTextId, isDefined } from '@fi-shared/utils';
import { ObjectTypes } from '@fi-shared/types';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { IChecklistV2ForList } from '@fi-shared/checklist/checklist-v2';

@Component({
    selector: 'fi-text-editor-insert-link-foldable',
    templateUrl: './text-editor-insert-link-foldable.component.html',
    styleUrl: './text-editor-insert-link-foldable.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-text-editor-insert-link-foldable' },
    providers: [{ provide: MAT_SELECT_CONFIG, useValue: { overlayPanelClass: 'insert-link-foldable-menu-panel' } }],
    standalone: false,
})
export class TextEditorInsertLinkFoldableComponent implements OnInit {
    @Input({ required: true }) linkToType: FiLinkToType;
    @Input({ required: true }) linkToValue: ITextItemLinkValue;
    @Input({ required: true }) items: INameIdObjWithChildren[];

    @Output() linkToValueChange = new EventEmitter<ITextItemLinkValue>();

    selectedItemIds: number[] = [];
    selectedChildItemIds: number[] = [];

    isSubmenuItemSelectionRequiredFn: (item: INameIdObjWithChildren) => boolean = (item: INameIdObjWithChildren) => {
        return !!item.ChildItems?.length;
    };

    constructor(
        private appTextService: AppTextService,
        private sharedActionsService: SharedActionsBaseService,
    ) {}

    ngOnInit(): void {
        this.setInitialItemSelection();
    }

    private getSelectedItem(): INameIdObjWithChildren {
        const selectedItemId = this.selectedItemIds.find((itemId) => isDefined(itemId));
        return this.items.find((item) => item.Id === selectedItemId);
    }
    private getSelectedChildItem(selectedItem: INameIdObjWithChildren): INameIdObj {
        const selectedChildItemId = this.selectedChildItemIds.find((childItemId) => isDefined(childItemId));
        return selectedItem.ChildItems.find((childItem) => childItem.Id === selectedChildItemId);
    }

    private setInitialItemSelection() {
        const objectType = this.linkToValue.ObjectType;
        if (objectType) {
            if (objectType === ObjectTypes.Checklist2 || objectType === ObjectTypes.Checklist2Audit) {
                const selectedItem = this.items.find((item) => item.Id === this.linkToValue.Id);
                if (selectedItem) {
                    this.selectedItemIds = [selectedItem.Id];
                    // check if name was changed
                    this.checkChangeName(selectedItem, undefined);
                } else {
                    // existing link data is not actual, checklist was deleted etc
                    this.resetLinkToValue();
                }
            } else if (
                objectType === ObjectTypes.ChecklistToSchedule ||
                objectType === ObjectTypes.ChecklistToAuditSchedule
            ) {
                const selectedItem = this.items.find(
                    (item) => !!item.ChildItems.find((childItem) => childItem.Id === this.linkToValue.Id),
                );
                const selectedChildItem: INameIdObj = selectedItem
                    ? selectedItem.ChildItems.find((childItem) => childItem.Id === this.linkToValue.Id)
                    : undefined;
                if (selectedItem && selectedChildItem) {
                    this.selectedItemIds = [selectedItem.Id];
                    this.selectedChildItemIds = [selectedChildItem.Id];
                    // check if name was changed
                    this.checkChangeName(selectedItem, selectedChildItem);
                } else {
                    // existing link data is not actual, checklist/schedule was deleted etc
                    this.resetLinkToValue();
                }
            } else {
                throw new Error(`unknown objectType in setInitialItemSelection: ${objectType}`);
            }
        }
    }

    private checkChangeName(item: INameIdObjWithChildren, childItem: INameIdObj) {
        const newName = this.getObjectName(item, childItem);
        if (this.linkToValue.Name !== newName) {
            // checklist's or schedule's name changed, update it
            this.linkToValueChange.emit({
                Id: this.linkToValue.Id,
                ObjectType: this.linkToValue.ObjectType,
                Name: newName,
            });
        }
    }

    protected getFoldableMenuLabel() {
        if (!this.linkToValue.ObjectType) {
            const objectType = this.getBaseObjectTypeByLinkToType();
            const chooseItemTextId = getObjectTypeNameTextId(objectType);
            return (
                this.appTextService.get('common.select') + ' ' + this.appTextService.get(chooseItemTextId).toLowerCase()
            );
        }

        return this.linkToValue.Name;
    }

    private getBaseObjectTypeByLinkToType() {
        if (this.linkToType === FiLinkToType.ChecklistV2) {
            return ObjectTypes.Checklist2;
        } else if (this.linkToType === FiLinkToType.ChecklistV2Audit) {
            return ObjectTypes.Checklist2Audit;
        } else {
            throw new Error(`unknown linkToType in getObjectTypeByLinkToType: ${this.linkToType}`);
        }
    }

    onMainItemsSelectionChanged(checklistIds: number[]) {
        this.onSelectionChanged();
    }

    onChildrenSelectionChange(scheduleIds: number[]) {
        if (!scheduleIds.length) {
            this.selectedItemIds = [];
        } else {
            const selectedChecklistId: number = this.getChecklistIdByScheduleId(scheduleIds[0]);
            if (isDefined(selectedChecklistId)) this.selectedItemIds = [selectedChecklistId];
        }
        this.onSelectionChanged();
    }

    getChecklistIdByScheduleId(scheduleId: number): number {
        return this.items.find((item: INameIdObjWithChildren) => {
            return item.ChildItems.some((childItem: INameIdObj) => childItem.Id === scheduleId);
        })?.Id;
    }

    /* logger
    showSelectedItemsData() {
    }

    private getSelectedChecklistData(): INameIdObjWithChildren | string {
        if (!this.selectedItemIds?.length) {
            return 'nothing is selected yet';
        }
        return this.items.find((item: INameIdObjWithChildren) => this.selectedItemIds.includes(item.Id));
    }

    private getSelectedScheduleData(): INameIdObj | string {
        if (!this.selectedItemIds?.length) {
            return 'nothing is selected yet';
        }
        const selectedChecklist = this.getSelectedChecklistData() as INameIdObjWithChildren;
        if (!selectedChecklist.ChildItems?.length) {
            return 'checklist without schedules';
        }
        return selectedChecklist.ChildItems.find((item: INameIdObj) => {
            return this.selectedChildItemIds.includes(item.Id);
        });
    }
    */

    onSelectionChanged() {
        const selectedItem = this.getSelectedItem();
        if (!selectedItem) {
            this.resetLinkToValue();
        } else {
            const selectedChildItem = this.getSelectedChildItem(selectedItem);

            let objectId: number;
            let objectType: ObjectTypes;
            let objectName = this.getObjectName(selectedItem, selectedChildItem);
            if (selectedChildItem) {
                objectId = selectedChildItem.Id;
                if (this.linkToType === FiLinkToType.ChecklistV2) {
                    objectType = ObjectTypes.ChecklistToSchedule;
                } else if (this.linkToType === FiLinkToType.ChecklistV2Audit) {
                    objectType = ObjectTypes.ChecklistToAuditSchedule;
                } else {
                    throw new Error(`unknown linkToType in onSelectionChanged: ${this.linkToType}`);
                }
            } else {
                objectId = selectedItem.Id;
                if (this.linkToType === FiLinkToType.ChecklistV2) {
                    objectType = ObjectTypes.Checklist2;
                } else if (this.linkToType === FiLinkToType.ChecklistV2Audit) {
                    objectType = ObjectTypes.Checklist2Audit;
                } else {
                    throw new Error(`unknown linkToType in onSelectionChanged: ${this.linkToType}`);
                }
            }
            this.linkToValueChange.emit({
                Id: objectId,
                ObjectType: objectType,
                Name: objectName,
            });
        }
    }

    private resetLinkToValue() {
        this.linkToValueChange.emit(undefined);
    }

    private getObjectName(item: INameIdObjWithChildren, childItem: INameIdObj) {
        return childItem ? `${item.Name} - ${childItem.Name}` : item.Name;
    }

    openFoldableExtendedSearch($event: MouseEvent) {
        $event.preventDefault();
        const objectType = this.getBaseObjectTypeByLinkToType();
        this.sharedActionsService.openExtendedSearch(objectType).then((result) => {
            if (!result) return;

            if (objectType === ObjectTypes.Checklist2 || objectType === ObjectTypes.Checklist2Audit) {
                const item = result as IChecklistV2ForList;
                if (item.ChecklistToScheduleId) {
                    this.selectedItemIds = [item.Id];
                    this.selectedChildItemIds = [item.ChecklistToScheduleId];
                } else {
                    this.selectedItemIds = [item.Id];
                    this.selectedChildItemIds = [];
                }
                this.onSelectionChanged();
            } else {
                throw new Error(`unknown objectType in openFoldableExtendedSearch: ${this.linkToType}`);
            }
        });
    }
}
